import { FooterSection, Header } from './sections';
import './sections/style.css';
import ReactGA from 'react-ga4';
import { useEffect } from 'react';
import SEO from '../../components/common/SEO';
import {breadcrumbSchema} from '../../utils/structuredData';

const PrivacyPolicy = () => {
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'production') {
      ReactGA.initialize('G-ZB9N7T58NE');
      ReactGA.send({ hitType: 'pageview', page: '/', title: 'Privacy policy Page' });
    }
  }, []);

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Privacy Policy', url: '/privacy-policy' }
  ]);

  return (
    <div className="min-w-full homePageContainer relative !bg-black">
      <SEO
        title="Privacy Policy - CadencePro.ai | Data Protection & Security"
        description="Read CadencePro's privacy policy to understand how we collect, use, and protect your personal information. We are committed to maintaining robust privacy protections."
        canonical="/privacy-policy"
        keywords="cadencepro privacy policy, data protection, user privacy, information security, GDPR compliance"
        structuredData={breadcrumbs}
      />
      <Header />
      <main className="w-full flex justify-center px-6 py-12 md:px-12 md:py-16 bg-gradient-to-b from-black via-gray-900/30 to-black">
        <div className="pointer-events-none absolute -top-6 left-2 w-72 h-48 opacity-40 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute bottom-[1050px] right-0 w-72 h-48 opacity-30 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-16 right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-[780px] right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-6 w-64 h-44 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute bottom-44 left-1/4 w-72 h-52 opacity-20 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />

        <article className="max-w-3xl w-full space-y-10 leading-7 text-gray-300">
          <header className="space-y-4 text-center pb-8 border-b border-gray-800">
            <h1 className="text-3xl md:text-4xl font-bold text-white">CADENCE PRO INC. PRIVACY POLICY</h1>
          </header>

          <section className="space-y-4">
            <p className="text-gray-300 text-base leading-relaxed">
              Cadence Pro Inc. (the "Company") is committed to maintaining robust privacy protections for its users. Our
              Privacy Policy ("Privacy Policy") is designed to help you understand how we collect, use and safeguard the
              information you provide to us and to assist you in making informed decisions when using our Service.
            </p>
            <p className="text-gray-300 text-base leading-relaxed">
              For purposes of this Agreement, "Site" refers to the Company's website, which can be accessed at
              https://cadencepro.ai. "Service" refers to the Company's services accessed via the Site, in which users can use
              the Cadencepro platform and Cadence Pro's related support and professional services.
            </p>
            <p className="text-gray-300 text-base leading-relaxed">
              The terms "we," "us," and "our" refer to the Company. "You" refers to you, as a user of our Site or our Service.
            </p>
            <p className="text-gray-300 text-base leading-relaxed">
              By accessing our Site or our Service, you accept our Privacy Policy and Terms of Use (found here:Terms of Use),
              and you consent to our collection, storage, use and disclosure of your Personal Information as described in this
              Privacy Policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">1. INFORMATION WE COLLECT</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              We collect "Non-Personal Information" and "Personal Information." Non-Personal Information includes
              information that cannot be used to personally identify you, such as anonymous usage data, general demographic
              information we may collect, referring/exit pages and URLs, platform types, preferences you submit and
              preferences that are generated based on the data you submit and number of clicks. Personal Information includes
              your first name, last name and email address, which you submit to us through the registration process at the Site.
            </p>
          </section>

          <section className="space-y-4 mt-2">
            <h3 className="text-lg md:text-xl font-semibold text-blue-400">1.1 Information collected via Technology</h3>
            <div className="space-y-3">
              <p className="text-gray-300 text-base leading-relaxed">
                To activate the Service you do not need to submit any Personal Information other than your first name, last name
                and email address. To use the Service thereafter, you do not need to submit further Personal Information.
                However, in an effort to improve the quality of the Service, we track information provided to us by your browser or
                by our software application when you view or use the Service, such as the website you came from (known as the
                "referring URL"), the type of browser you use, the device from which you connected to the Service, the time and
                date of access, and other information that does not personally identify you. We track this information using
                cookies, or small text files which include an anonymous unique identifier. Cookies are sent to a user's browser
                from our servers and are stored on the user's computer hard drive. Sending a cookie to a user's browser enables
                us to collect Non-Personal information about that user and keep a record of the user's preferences when utilizing
                our services, both on an individual and aggregate basis. For example, the Company may use cookies to collect the
                following information:
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                The Company may use both persistent and session cookies; persistent cookies remain on your computer after you
                close your session and until you delete them, while session cookies expire when you close your browser.
              </p>
            </div>
          </section>

          <section className="space-y-4 mt-2">
            <h3 className="text-lg md:text-xl font-semibold text-blue-400">1.2 Information you provide us by registering for an account</h3>
            <div>
              <p className="text-gray-300 text-base leading-relaxed">
                In addition to the information provided automatically by your browser when you visit the Site, to become a
                subscriber to the Service you will need to create a personal profile. You can create a profile by registering with the
                Service and entering your email address, and creating a user name and a password. By registering, you are
                authorizing us to collect, store and use your email address in accordance with this Privacy Policy.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">2. HOW WE USE AND SHARE INFORMATION</h2>
            <h3 className="text-lg md:text-xl font-semibold text-blue-400 mt-4">2.1 Personal Information</h3>
            <p className="text-gray-300 text-base leading-relaxed">
              Except as otherwise stated in this Privacy Policy, we do not sell, trade, rent or otherwise share for marketing
              purposes your Personal Information with third parties without your consent. We do share Personal Information
              with vendors who are performing services for the Company, such as the servers for our email communications
              who are provided access to the user's email address for purposes of sending emails from us. Those vendors use
              your Personal Information only at our direction and in accordance with our Privacy Policy.
            </p>
            <p className="text-gray-300 text-base leading-relaxed">
              In general, the Personal Information you provide to us is used to help us communicate with you. For example, we
              use Personal Information to contact users in response to questions, solicit feedback from users, provide technical
              support, and inform users about promotional offers.
            </p>
            <p className="text-gray-300 text-base leading-relaxed">
              We may share Personal Information with outside parties if we have a good-faith belief that access, use,
              preservation or disclosure of the information is reasonably necessary to meet any applicable legal process or
              enforceable governmental request; to enforce applicable Terms of Service, including investigation of potential
              violations; address fraud, security or technical concerns; or to protect against harm to the rights, property, or
              safety of our users or the public as required or permitted by law.
            </p>
          </section>

          <section className="space-y-4 mt-2">
            <h3 className="text-lg md:text-xl font-semibold text-blue-400">2.2 Non-Personal Information</h3>
            <div className="space-y-3">
              <p className="text-gray-300 text-base leading-relaxed">
              In general, we use Non-Personal Information to help us improve the Service and customize the user experience.
              We also aggregate Non-Personal Information in order to track trends and analyze use patterns on the Site. This
              Privacy Policy does not limit in any way our use or disclosure of Non-Personal Information and we reserve the
              right to use and disclose such Non-Personal Information to our partners, advertisers and other third parties at our
              discretion.
            </p>
              <p className="text-gray-300 text-base leading-relaxed">
                In the event we undergo a business transaction such as a merger, acquisition by another company, or sale of all
                or a portion of our assets, your Personal Information may be among the assets transferred. You acknowledge and
                consent that such transfers may occur and are permitted by this Privacy Policy, and that any acquirer of our assets
                may continue to process your Personal Information as set forth in this Privacy Policy. If our information practices
                change at any time in the future, we will post the policy changes to the Site so that you may opt out of the new
                information practices. We suggest that you check the Site periodically if you are concerned about how your
                information is used.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">3. HOW WE PROTECT INFORMATION</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              We implement security measures designed to protect your information from unauthorized access. Your account is
              protected by your account password and we urge you to take steps to keep your personal information safe by not
              disclosing your password and by logging out of your account after each use. We further protect your information
              from potential security breaches by implementing certain technological security measures including encryption,
              firewalls and secure socket layer technology. However, these measures do not guarantee that your information
              will not be accessed, disclosed, altered or destroyed by breach of such firewalls and secure server software. By
              using our Service, you acknowledge that you understand and agree to assume these risks.
            </p>
          </section>


          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">4. DATA RETENTION</h2>
            <div className="space-y-3">
              <p className="text-gray-300 text-base leading-relaxed">
                We retain personal data only for as long as necessary to provide and improve our services.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                Account information is kept while your account is active and for a reasonable period afterward to meet legal
                obligations and resolve disputes.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                Calendar data is used only to provide synchronization and is deleted within a reasonable timeframe after you
                disconnect your calendar or delete your account.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                Usage and technical data is retained for a limited period for analytics, security, and performance monitoring.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                We may retain certain information for longer where required or permitted by law. When data is no longer needed,
                we delete it.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">5. YOUR RIGHTS FOR USE OF PERSONAL INFORMATION</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              You have the right at any time to prevent us from contacting you for marketing purposes. When we send a
              promotional communication to a user, the user can opt out of further promotional communications by following the
              unsubscribe instructions provided in each promotional email. You can also indicate that you do not wish to receive
              marketing communications by sending us an email. Please note that notwithstanding the promotional preferences
              you indicate by emailing us, we may continue to send you administrative emails including, for example, periodic
              updates to our Privacy Policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">6. LINKS TO OTHER WEBSITES</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              As part of the Service, we may provide links to or compatibility with other websites or applications. However, we
              are not responsible for the privacy practices employed by those websites or the information or content they
              contain. This Privacy Policy applies solely to information collected by us through the Site and the Service.
              Therefore, this Privacy Policy does not apply to your use of a third party website accessed by selecting a link on
              our Site or via our Service. To the extent that you access or use the Service through or on another website or
              application, then the privacy policy of that other website or application will apply to your access or use of that site
              or application. We encourage our users to read the privacy statements of other websites before proceeding to use
              them.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">7. CHANGES TO OUR PRIVACY POLICY</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              The Company reserves the right to change this policy and our Terms of Service at any time. We will notify you of
              significant changes to our Privacy Policy by sending a notice to the primary email address specified in your account
              or by placing a prominent notice on our site. Significant changes will go into effect 30 days following such
              notification. Non-material changes or clarifications will take effect immediately. You should periodically check the
              Site and this privacy page for updates.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3">8. CONTACT US</h2>
            <p className="text-gray-300 text-base leading-relaxed">
              If you have any questions regarding this Privacy Policy or the practices of this Site, please contact us by sending
              an email to <a href="mailto:cadenceproinc@gmail.com" className="text-blue-500 hover:text-blue-400 underline">cadenceproinc@gmail.com</a>.
            </p>
          </section>

          <footer className="text-center pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-500">Last Updated: This Privacy Policy was last updated on March 17, 2026</p>
          </footer>
        </article>
      </main>
      <FooterSection />
    </div>
  );
};

export default PrivacyPolicy;
